/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { formatDate } from '@squiz/dxp-console-support';
import {
  CUSTOM_ICON_FOLDER_COLOUR,
  ICON_GPP_BAD,
  ICON_HOURGLASS_BOTTOM,
  ICON_POLICY,
  ICON_SHIELD,
  ICON_VERIFIED_USER,
  SdsIcon,
} from '@squiz/sds';
import { formatBytes } from 'apps/monitoring/src/Utilities/FormatBytes/FormatBytes';
import clsx from 'clsx';
import { ApiTable, ApiTableFetchOptions, ApiTableProps, ApiTableRef, TableCell } from 'libs/sds/src/Components';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { AccessControlListValues, useFileStore, VirusScanStatusType } from '../../../../../../Services/file-store';
import { FILE_EXPLORER_STRINGS } from '../../FileExplorer.constants';
import { useFileExplorerStore } from '../../FileExplorer.state';
import { FileExplorerFileIcon } from '../FileExplorerFileIcon/FileExplorerFileIcon';
import { FileExplorerItemActionMenu } from '../FileExplorerItemActionMenu/FileExplorerItemActionMenu';
import { FileExplorerTableProps } from './FileExplorerTable.defs';

export const FileExplorerTable = ({ showFileId }: FileExplorerTableProps): JSX.Element => {
  const apiTableRef = useRef<ApiTableRef>(null);
  const { t } = useTranslation();
  const {
    stagedFilesToUpload,
    setStagedFilesToUpload,
    directoryContents,
    setDirectoryContents,
    fileExplorerPosition,
    setFileExplorerPosition,
    stagedFileExplorerPosition,
    setStagedFileExplorerPosition,
    setSelectedRows,
    clearSelectedRows,
    setClearSelectedRows,
  } = useFileExplorerStore();
  const { fileUpload, loadingState, getDirectoryContents } = useFileStore();

  const fetchFiles = useCallback(
    async (options?: ApiTableFetchOptions) => {
      setClearSelectedRows(true);
      const fileList = await getDirectoryContents({ source: stagedFileExplorerPosition });
      setDirectoryContents(fileList.data || []);
      setFileExplorerPosition(stagedFileExplorerPosition);
    },
    [
      getDirectoryContents,
      setClearSelectedRows,
      setDirectoryContents,
      setFileExplorerPosition,
      stagedFileExplorerPosition,
    ],
  );

  const uploadStagedFiles = useCallback(async (): Promise<void> => {
    await fileUpload({
      files: stagedFilesToUpload.map((file) => {
        return {
          access: AccessControlListValues.PUBLIC_READ,
          file,
          source: `${fileExplorerPosition + file.name}`,
        };
      }),
    });
    setStagedFilesToUpload([]);
    await fetchFiles();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUpload, setStagedFilesToUpload, stagedFilesToUpload]);

  useEffect(() => {
    if (stagedFilesToUpload.length) {
      uploadStagedFiles();
    }
  }, [fileUpload, setStagedFilesToUpload, stagedFilesToUpload, uploadStagedFiles]);

  useEffect(() => {
    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagedFileExplorerPosition]);

  useEffect(() => {
    if (clearSelectedRows && apiTableRef.current) {
      apiTableRef.current.clearSelection();
      setSelectedRows([]);
      setClearSelectedRows(false);
    }
  }, [clearSelectedRows, setClearSelectedRows, setSelectedRows]);

  // reflecting the behavior in test environment is not working - but it works!
  // istanbul ignore next
  const onDrop = useCallback(
    (acceptedFiles: Array<File>) => {
      setStagedFilesToUpload(acceptedFiles);
    },
    [setStagedFilesToUpload],
  );

  const changeDirectory = useCallback(
    async (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      // istanbul ignore next
      const controlCell = target.closest('tr')?.querySelector('.cell-name');
      const isTargetActionable = target.closest('.actionable');
      if (!controlCell || isTargetActionable) {
        return;
      }

      const path = controlCell.getAttribute('data-path') as string;
      const type = controlCell.getAttribute('data-type') as string;

      if (type !== 'DIRECTORY') {
        return;
      }

      setStagedFileExplorerPosition(path);
    },
    [setStagedFileExplorerPosition],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ noClick: true, onDrop });

  const labels = {
    actions: t(FILE_EXPLORER_STRINGS.actions),
    directoryIsEmpty: t(FILE_EXPLORER_STRINGS.directoryIsEmpty),
    dragAndDropArea: t(FILE_EXPLORER_STRINGS.dragAndDropArea),
    dropFilesHere: t(FILE_EXPLORER_STRINGS.dropFilesHere),
    extension: t(FILE_EXPLORER_STRINGS.extension),
    fileSize: t(FILE_EXPLORER_STRINGS.fileSize),
    lastUpdated: t(FILE_EXPLORER_STRINGS.lastUpdated),
    name: t(FILE_EXPLORER_STRINGS.name),
    private: t(FILE_EXPLORER_STRINGS.private),
    public: t(FILE_EXPLORER_STRINGS.public),
    type: t(FILE_EXPLORER_STRINGS.type),
    virusScan: t(FILE_EXPLORER_STRINGS.virusScan),
    virusStatusQuarantined: t(FILE_EXPLORER_STRINGS.virusStatusQuarantined),
    virusStatusScanned: t(FILE_EXPLORER_STRINGS.virusStatusScanned),
    virusStatusUnknown: t(FILE_EXPLORER_STRINGS.virusStatusUnknown),
    virusStatusUnscanned: t(FILE_EXPLORER_STRINGS.virusStatusUnscanned),
    visibility: t(FILE_EXPLORER_STRINGS.visibility),
  };

  const tableColumns: Array<TableCell> = [
    {
      content: labels.name,
      key: 'name',
    },
    {
      content: labels.type,
      key: 'type',
    },
    {
      content: labels.visibility,
      key: 'visibility',
    },
    {
      content: labels.fileSize,
      key: 'size',
    },
    {
      content: labels.lastUpdated,
      key: 'lastUpdated',
    },
    {
      align: 'centre',
      content: (
        <SdsIcon
          svg={
            <svg className="file-explorer__virus-icon file-explorer__virus-icon--header">
              <title>{labels.virusScan}</title>
              {ICON_SHIELD}
            </svg>
          }
        ></SdsIcon>
      ),
      key: 'virusScan',
    },
    {
      actionable: true,
      content: '',
      key: 'actions',
    },
  ];

  const tableRows: Array<{}> = directoryContents.map((item, i) => {
    const { id, path, type, virusScanStatus, urls } = item;
    const name = path
      .replace(fileExplorerPosition, '')
      .replace(/\.[^.]+$/, '')
      .replace(/\+/g, ' ')
      .replace(type === 'DIRECTORY' ? /\/$/ : '', '');
    const extension = path.split('.').length > 1 ? path.split('.').pop() : '--';
    const shouldDirectoryBeUp = directoryContents.length > 7 && i > directoryContents.length - 5;
    // tested, but not reflecting in coverage
    // istanbul ignore next
    const visibilityString = item?.access ?? '--';
    const rowIcon =
      type === 'DIRECTORY' ? (
        <SdsIcon svg={CUSTOM_ICON_FOLDER_COLOUR} />
      ) : (
        <FileExplorerFileIcon contentType={item.contentType} />
      );
    let virusScanIcon;
    let virusStatusLabel = '';
    if (item.virusScanStatus === VirusScanStatusType.Scanned) {
      virusScanIcon = ICON_VERIFIED_USER;
      virusStatusLabel = labels.virusStatusScanned;
    } else if (item.virusScanStatus === VirusScanStatusType.Unknown) {
      virusScanIcon = ICON_POLICY;
      virusStatusLabel = labels.virusStatusUnknown;
    } else if (item.virusScanStatus === VirusScanStatusType.Quarantined) {
      virusScanIcon = ICON_GPP_BAD;
      virusStatusLabel = labels.virusStatusQuarantined;
    } else {
      // Scan in progress
      virusScanIcon = ICON_HOURGLASS_BOTTOM;
      virusStatusLabel = labels.virusStatusUnscanned;
    }

    return {
      actions: (
        <FileExplorerItemActionMenu
          contentType={item.contentType}
          dropdownUp={shouldDirectoryBeUp}
          id={id}
          name={name}
          path={path}
          type={type}
          urls={urls}
          virusScanStatus={virusScanStatus}
          visibility={item.access!}
        />
      ),
      key: `table-row-${i}`,
      lastUpdated: (
        <span className="cell-last-modified">
          {item.lastModified ? formatDate(new Date(item.lastModified).toString(), true) : '--'}
        </span>
      ),
      name: (
        <span
          className="cell-name"
          data-content-type={item.contentType}
          data-extension={extension}
          data-path={path}
          data-size={item.size ? formatBytes(item.size) : undefined}
          data-type={type}
          data-visibility={item.access}
          id={id}
        >
          {rowIcon}{' '}
          <div>
            <div>{name}</div>
            <div className="file-explorer__file-id">{showFileId && id}</div>
          </div>
        </span>
      ),
      size: <span className="cell-size">{item.size ? formatBytes(item.size) : '--'}</span>,
      type: <span className="cell-type">{type === 'OBJECT' ? extension : 'folder'}</span>,
      virusScan: (
        <span className="cell-virus-scan">
          {type === 'DIRECTORY' && <>--</>}
          {type === 'OBJECT' && (
            <SdsIcon
              svg={
                <svg className="file-explorer__virus-icon">
                  <title>{virusStatusLabel}</title>
                  {virusScanIcon}
                </svg>
              }
            ></SdsIcon>
          )}
        </span>
      ),
      visibility: <span className="cell-visibility">{visibilityString}</span>,
    };
  });

  const tableProps: ApiTableProps = useMemo(() => {
    return {
      classes: 'file-explorer__table',
      // Special case - Implemented in useEffect instead
      fetchData: async (): Promise<void> => {},
      initialFetch: true,
      isPaginated: false,
      isSelectable: true,
      isSlim: true,
      loading: loadingState.directoryContentsLoading,
      onDoubleClick: changeDirectory,
      onRowSelect: setSelectedRows,
      tableContent: {
        columns: tableColumns,
        rows: tableRows,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directoryContents, loadingState.directoryContentsLoading, showFileId]);

  return (
    <>
      <div
        {...getRootProps({
          'aria-label': labels.dragAndDropArea,
          className: clsx('file-explorer__file-dropzone', {
            'file-explorer__file-dropzone--drag-active': isDragActive,
          }),
          role: 'button',
        })}
      >
        <input {...getInputProps()} />
        <p className="file-explorer__file-dropzone-text">{labels.dropFilesHere}</p>
        <ApiTable {...tableProps} ref={apiTableRef} />
      </div>
    </>
  );
};
